import React, { useEffect, useRef, useState } from 'react';
import { ref, onValue } from 'firebase/database';
import { db, auth } from '../../../integrations/firebase';
import { CheckCircle2, Circle, Loader2, XCircle, X, PhoneOff } from 'lucide-react';
import { DIAL_EVENT, cancelDial } from '../../../core/telephony/dialViaPhone';
import { DIAL_STEPS, dialProgress, stepStates, deliveryText } from '../../../core/telephony/phoneHealth';
import { usePhonePresence } from '../../../core/telephony/usePhonePresence';
import { PhoneReasons } from './PhoneStatusPill';

// DialTracker — "did my phone actually get the call?", answered step by step.
//
// dialViaPhone announces each call on a window event; this card follows the
// command node the phone writes to (tc/commands/{uid}/{cmdId}/phone) and shows
// Sent → Received → Dialling → On call → Ended live. If the phone stays silent
// past the ack window it says so in red, with the phone's last known condition,
// and offers Cancel. A command the phone will no longer dial (over a minute old)
// is cancelled automatically so the agent is never left "on a call" that never
// happened.

const AUTO_CANCEL_MS = 75_000;   // the phone refuses commands older than 60 s

const StepIcon = ({ state }) => {
    if (state === 'done') return <CheckCircle2 size={15} className="text-green-600 shrink-0" />;
    if (state === 'active') return <Loader2 size={15} className="text-amber-500 animate-spin shrink-0" />;
    if (state === 'failed') return <XCircle size={15} className="text-red-600 shrink-0" />;
    return <Circle size={15} className="text-slate-300 shrink-0" />;
};

const DialTracker = () => {
    const [dial, setDial] = useState(null);       // the latest dial event
    const [cmd, setCmd] = useState(null);         // live command node
    const [late, setLate] = useState(false);      // ack window passed with no word
    const [cancelState, setCancelState] = useState('');
    const [hidden, setHidden] = useState(false);
    const { view } = usePhonePresence();
    const timers = useRef([]);

    useEffect(() => {
        window.__dakshaDialTracker = true;
        const onDial = (e) => {
            timers.current.forEach(clearTimeout); timers.current = [];
            setDial(e.detail); setCmd(null); setLate(false); setCancelState(''); setHidden(false);
        };
        window.addEventListener(DIAL_EVENT, onDial);
        return () => { window.__dakshaDialTracker = false; window.removeEventListener(DIAL_EVENT, onDial); timers.current.forEach(clearTimeout); };
    }, []);

    // Follow the command the phone is working on.
    const cmdId = dial?.phase === 'sent' ? dial.cmdId : null;
    useEffect(() => {
        const uid = auth.currentUser?.uid;
        if (!cmdId || !uid) return undefined;
        const unsub = onValue(ref(db, `tc/commands/${uid}/${cmdId}`), s => setCmd(s.val() || null), () => {});
        if (dial.expectAck) {
            timers.current.push(setTimeout(() => setLate(true), dial.ackTimeoutMs || 8000));
            timers.current.push(setTimeout(async () => {
                const r = await cancelDial(cmdId, { quiet: true });
                if (r.cancelled) setCancelState('auto');
            }, AUTO_CANCEL_MS));
        }
        return () => unsub();
    }, [cmdId]); // eslint-disable-line react-hooks/exhaustive-deps

    const prog = dialProgress(cmd);

    // Stop the clocks once the phone has spoken; fade out after a finished call.
    useEffect(() => {
        if (!cmdId) return;
        if (prog.acked || prog.failed) { timers.current.forEach(clearTimeout); timers.current = []; }
        if (prog.failed && cmd?.status === 'queued') cancelDial(cmdId, { quiet: true });
        if (prog.step === 'in_progress' || prog.step === 'ended') {
            const t = setTimeout(() => setHidden(true), prog.step === 'ended' ? 6000 : 5000);
            return () => clearTimeout(t);
        }
    }, [prog.step, prog.acked, prog.failed]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (dial && (dial.phase === 'refused')) {
            const t = setTimeout(() => setHidden(true), 7000);
            return () => clearTimeout(t);
        }
        return undefined;
    }, [dial]);

    if (!dial || hidden || dial.phase === 'checking') return null;

    const who = dial.name || dial.mobile;
    const close = () => setHidden(true);
    const shell = (tone, body) => (
        <div className={`fixed bottom-4 left-1/2 -translate-x-1/2 md:left-auto md:translate-x-0 md:right-[390px] z-[95] w-[340px] max-w-[94vw] rounded-2xl border shadow-2xl bg-white overflow-hidden ${tone}`}>
            {body}
        </div>
    );
    const header = (text, cls) => (
        <div className={`px-4 py-2.5 flex items-center gap-2 text-sm font-semibold ${cls}`}>
            <span className="truncate">{text}</span>
            <button type="button" onClick={close} className="ml-auto p-1 rounded hover:bg-black/5" aria-label="Close"><X size={14} /></button>
        </div>
    );

    // Never sent: phone offline, or the server refused.
    if (dial.phase === 'offline' || dial.phase === 'refused') {
        const v = dial.view || view;
        return shell('border-red-200', (
            <>
                {header(dial.phase === 'offline' ? `Call not sent — ${v?.headline || 'phone offline'}` : 'Call not placed', 'bg-red-50 text-red-700')}
                <div className="p-4 space-y-2 text-sm">
                    <div className="text-slate-700">{who ? <b>{who}</b> : null} {dial.phase === 'refused' ? `— ${dial.error}` : ''}</div>
                    {dial.phase === 'offline' && v && v.reasons.length > 0 && <PhoneReasons view={v} />}
                    {dial.phase === 'offline' && <div className="text-[11px] text-slate-400">Fix it on the phone (open Daksha Caller → More → Phone connection), then click call again.</div>}
                </div>
            </>
        ));
    }

    // Sent: live steps.
    const failedTitle = cancelState === 'auto'
        ? 'Phone never answered — call cancelled'
        : prog.failed ? `Call failed — ${prog.reason}` : null;
    const silent = !prog.acked && !prog.failed && late;
    const headerText = failedTitle
        || (silent ? 'Phone did not respond — check the app'
            : prog.step === 'ended' ? (prog.answered === false ? `Not answered · ${who}` : `Call ended · ${who}`)
                : prog.step === 'in_progress' ? `On call with ${who}`
                    : prog.acked ? `Phone is calling ${who}…` : `Sending to your phone… ${who}`);
    const states = stepStates(prog, silent || cancelState === 'auto');
    const tone = failedTitle || silent ? 'bg-red-50 text-red-700' : prog.step === 'in_progress' || prog.step === 'ended' ? 'bg-green-50 text-green-800' : 'bg-amber-50 text-amber-800';

    return shell(failedTitle || silent ? 'border-red-200' : 'border-slate-200', (
        <>
            {header(headerText, tone)}
            <div className="p-4 space-y-2 text-sm">
                {!dial.expectAck ? (
                    <div className="text-xs text-amber-700">Sent. Your phone runs an old Daksha Caller that cannot confirm calls — update it to see each step here.</div>
                ) : (
                    <ul className="space-y-1.5">
                        {DIAL_STEPS.map((s, i) => {
                            const st = states[i];
                            let label = s.label;
                            if (s.key === 'received' && st === 'done' && deliveryText(prog)) {
                                label = `${s.label} — ${deliveryText(prog)}`;
                            }
                            if (s.key === 'ended' && prog.step === 'ended' && Number.isFinite(prog.talkSec)) {
                                label = prog.talkSec > 0 ? `Call ended · talked ${Math.floor(prog.talkSec / 60)}:${String(prog.talkSec % 60).padStart(2, '0')}` : 'Call ended · not answered';
                            }
                            return (
                                <li key={s.key} className={`flex items-center gap-2 ${st === 'todo' ? 'text-slate-400' : st === 'failed' ? 'text-red-700 font-semibold' : 'text-slate-700'}`}>
                                    <StepIcon state={st} /> {label}
                                </li>
                            );
                        })}
                    </ul>
                )}
                {silent && (
                    <div className="rounded-lg bg-red-50 border border-red-100 p-2.5 space-y-1.5">
                        <div className="text-xs text-red-700">Nothing back from the phone{view ? ` (${view.headline.toLowerCase()})` : ''}. Check that Daksha Caller is open and the phone has internet.</div>
                        {view && view.reasons.length > 0 && <PhoneReasons view={view} />}
                        {cancelState !== 'done' && cancelState !== 'auto' && (
                            <button type="button" disabled={cancelState === 'busy'}
                                onClick={async () => { setCancelState('busy'); const r = await cancelDial(cmdId, { quiet: true }); setCancelState(r.cancelled ? 'done' : r.acked ? '' : 'error'); }}
                                className="flex items-center gap-1.5 rounded-lg bg-red-600 text-white px-3 py-1.5 text-xs font-semibold hover:bg-red-700 disabled:opacity-50">
                                <PhoneOff size={13} /> {cancelState === 'busy' ? 'Cancelling…' : 'Cancel this call'}
                            </button>
                        )}
                        {cancelState === 'done' && <div className="text-xs text-slate-600">Cancelled — you can dial again.</div>}
                        {cancelState === 'error' && <div className="text-xs text-red-700">Could not cancel — check this computer's network.</div>}
                    </div>
                )}
            </div>
        </>
    ));
};

export default DialTracker;
